import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, svgCount } from '../skillUsage';

export const svgIconEnrichmentSkill: CodeoSkill = {
    name: SKILL_NAMES.svgIconEnrichment,
    kind: 'codeo',
    description:
        'Animate the moving parts inside an icon so it feels alive — a rotating clock hand, a ' +
        'pulsing record dot, dashes flowing along a data line. For icon-led feature and benefit moments.',
    documentUsesSkill: check(
        (script, markup) =>
            svgCount(markup) >= 1 &&
            (/setAttribute\(\s*['"]transform/.test(script) ||
                /svgOrigin/.test(script) ||
                (/onUpdate/.test(script) && /rotate\(|scale\(/.test(script))),
    ),
    instructions: `# SVG Icon Enrichment

Treat an icon as a machine with moving parts, not a flat picture. The clock's hand sweeps, the record dot pulses, dashes flow along the data line. A row of static icons sits dead on screen; the same icons with their internals animating feel alive. Distinct from \`${SKILL_NAMES.svgDrawOn}\` (which draws the *outline*) — enrichment animates the *internal parts*, ideally after the outline has drawn. Pair with \`${SKILL_NAMES.ambientFloatLoop}\` to float the whole icon while its parts move.

## Markup

Author the icon with named inner elements so the timeline can target each part.

\`\`\`html
<svg id="clock" width="160" height="160" viewBox="0 0 120 120"
     style="position: absolute; left: 600px; top: 420px;">
  <circle cx="60" cy="60" r="50" fill="none" stroke="var(--l5-color-1, #7a6dff)" stroke-width="6"/>
  <line id="hand-min" x1="60" y1="60" x2="60" y2="24" stroke="var(--l5-color-0, #101820)" stroke-width="6" stroke-linecap="round"/>
  <circle cx="60" cy="60" r="6" fill="var(--l5-color-0, #101820)"/>
</svg>
\`\`\`

## Timeline code

Each part is driven by a finite, seek-safe \`onUpdate\` writing a transform every frame. **Crucial**: rotate/scale inner SVG parts with the SVG \`transform\` *attribute* (origin in viewBox units), not CSS \`transform\` + \`transform-origin\` — see Pitfalls.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const DUR = 6;

// Rotation — clock hand sweeps, origin at the dial center in viewBox units
const min = document.querySelector('#hand-min');
const minState = { deg: 0 };
tl.to(minState, { deg: 360 * 1.5, duration: DUR, ease: 'none', onUpdate: () => {
    min.setAttribute('transform', \`rotate(\${minState.deg} 60 60)\`);
} }, 0);

// Pulse — record dot breathes via sine; finite cycles across the on-screen window
const dot = document.querySelector('#rec-dot');
const pulse = { p: 0 };
tl.to(pulse, { p: Math.PI * 2 * 3, duration: DUR, ease: 'none', onUpdate: () => {
    const s = 1 + Math.sin(pulse.p) * 0.12;
    dot.setAttribute('transform', \`translate(60 60) scale(\${s}) translate(-60 -60)\`);
} }, 0);

// Dash flow — dashes stream along a data line; offset is a multiple of the dash period so the end frame doesn't jump
const flow = document.querySelector('#data-flow'); // stroke-dasharray="14 12" in markup
const flowState = { offset: 0 };
tl.to(flowState, { offset: -260, duration: DUR, ease: 'none', onUpdate: () => {
    flow.style.strokeDashoffset = String(flowState.offset);
} }, 0);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Rotation**: minute hand 0.5–2 revolutions across the moment; a second hand should be visibly faster (>3×). Avoid whole-number revolutions if the end frame matters — it lands back where it started.
- **Pulse**: 2–4 cycles over a 3–5s moment; scale amplitude 0.05 (breathing) to 0.20 (throbbing). For a ring + dot, lag the ring behind the dot by π/2 so it reads as a ripple.
- **Dash flow**: negative offset flows left→right, positive right→left; keep |offset| an integer multiple of (dash + gap) so the loop end has no phase jump.
- **Outline-then-enrich chain**: draw the outline first via \`${SKILL_NAMES.svgDrawOn}\`, start the enrichment when the draw completes — the icon "wakes up" after assembling.
- Phase-offset parts of the same icon (hands at different speeds, ring vs dot offset) — perfectly synced parts look mechanical; offset looks alive.

## Pitfalls

- **❗ For rotation/scale around a point inside an SVG, use the SVG \`transform\` attribute** (\`el.setAttribute('transform', 'rotate(deg cx cy)')\`), NOT CSS \`transform\` + \`transform-origin\`. CSS interprets the origin in the element's own bbox-local coordinates, so for a thin \`<line>\` "60 60" lands *outside* the line and the hand flies on an off-center arc.
- **Finite repeats only** — drive continuous motion with a tweened proxy + \`onUpdate\` as above (never CSS \`@keyframes\`, \`requestAnimationFrame\`, or an infinite GSAP repeat), so frame-by-frame seeking and rendering stay deterministic.
- Keep amplitudes subtle — icons are decorative, not headlines. A throbbing icon next to body text steals the read.
- \`onUpdate\` must be O(1) — it runs on every seeked frame; cache the element references outside the callback (as above).
- The icon as a whole can be floated by \`${SKILL_NAMES.ambientFloatLoop}\`, but the inner-part transforms are owned by these onUpdates — don't also put a CSS transform on the same inner elements.`,
};
